import React, { useState, useEffect } from 'react'
import { colors } from './fieldTheme'
import { plural } from './fieldReference'
import { URGENCIES, defaultAssignee, teamsFromUsers, userAssignee, restockDescription, formBlocker, kindTitle, buildIntervention, memoEvents } from './fieldInterventions'
import { nowSeconds } from './fieldDates'
import { CheckIcon, ChevronRight, WrenchIcon, InfoIcon, WarningIcon } from './FieldIcons'
import { ActionSheet, BottomSheet, SheetGroup, SheetRow, PrimaryButton, ErrorNote, SoftSearchField } from './FieldUI'
import FieldPhotoPicker from './FieldPhotoPicker'

// Créer une intervention depuis un inventaire (décisions 30, 42 et 43), dans un volet :
// choix Réassort / Réparation, puis formulaire allégé. Signaler un problème pendant l'inventaire
// ouvre directement le formulaire, avec le choix Ménage ou Technique.

const sectionTitle = { fontSize: 15, fontWeight: 700, color: colors.textSecondary, letterSpacing: '.04em', textTransform: 'uppercase', margin: 0, paddingTop: 8 }

function ChoiceRow({ title, subtitle, color, disabled, onClick })
{
	return (
		<button
			type="button"
			className="field-row"
			disabled={disabled}
			onClick={onClick}
			style={{ backgroundImage: 'none', backgroundColor: disabled ? '#E9ECF1' : colors.surface, minHeight: 88, width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: 16, borderRadius: 18, border: '2px solid ' + colors.border, color: disabled ? '#5B6577' : colors.text, textAlign: 'left', fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer' }}
		>
			<span aria-hidden="true" style={{ width: 56, height: 56, borderRadius: 999, background: disabled ? '#9AA3B8' : color, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
				<WrenchIcon size={32} />
			</span>
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
				<span style={{ fontSize: 19, fontWeight: 800 }}>{title}</span>
				<span style={{ fontSize: 15, fontWeight: 600, color: disabled ? '#5B6577' : colors.textSecondary }}>{subtitle}</span>
			</span>
			{!disabled && <ChevronRight color={colors.primary} strokeWidth={2.8} />}
		</button>
	)
}

function Pill({ selected, label, icon, onClick })
{
	return (
		<button
			type="button"
			aria-pressed={selected}
			onClick={onClick}
			style={{ backgroundImage: 'none', backgroundColor: selected ? colors.primary : colors.surface, flex: 1, minWidth: 0, minHeight: 56, borderRadius: 999, border: '2px solid ' + (selected ? colors.primary : colors.border), color: selected ? '#FFFFFF' : colors.text, fontSize: 17, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0 8px', fontFamily: 'inherit', cursor: 'pointer' }}
		>
			{selected ? <CheckIcon size={20} /> : icon}
			{label}
		</button>
	)
}

function CheckRow({ checked, title, subtitle, onClick })
{
	return (
		<button
			type="button"
			role="checkbox"
			aria-checked={checked}
			onClick={onClick}
			style={{ backgroundImage: 'none', backgroundColor: 'transparent', minHeight: 64, width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '8px 0', border: 0, borderTop: '1px solid ' + colors.separator, color: colors.text, textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' }}
		>
			<span aria-hidden="true" style={{ width: 32, height: 32, borderRadius: 8, border: '2px solid ' + (checked ? colors.primary : colors.border), background: checked ? colors.primary : colors.surface, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
				{checked && <CheckIcon size={20} />}
			</span>
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
				<span style={{ fontSize: 17, fontWeight: 700 }}>{title}</span>
				<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{subtitle}</span>
			</span>
		</button>
	)
}

function AssigneePicker({ value, users, onPick, onClose })
{
	const [query, setQuery] = useState('')
	const needle = query.trim().toLowerCase()
	const teams = teamsFromUsers(users).filter(name => !needle || name.toLowerCase().includes(needle))
	const people = (users || []).filter(user => !needle || ((user.firstname || '') + ' ' + (user.lastname || '')).toLowerCase().includes(needle))
	return (
		<BottomSheet title="Attribuer à" full onClose={onClose}>
			<SoftSearchField id="field-assignee-search" label="Chercher une équipe ou une personne" placeholder="Chercher" value={query} onChange={setQuery} onClear={() => setQuery('')} />
			{teams.length > 0 && <h3 style={sectionTitle}>Équipes</h3>}
			{teams.length > 0 && (
				<SheetGroup>
					{teams.map(name => <SheetRow key={'team-' + name} label={name} icon={value.type === 'team' && value.id === name ? <CheckIcon color={colors.primary} /> : null} onClick={() => onPick({ type: 'team', id: name, name })} />)}
				</SheetGroup>
			)}
			{people.length > 0 && <h3 style={sectionTitle}>Personnes</h3>}
			{people.length > 0 && (
				<SheetGroup>
					{people.map(user => <SheetRow key={user._id} label={userAssignee(user).name || user.email || 'Sans nom'} icon={value.type === 'employee' && value.id === user._id ? <CheckIcon color={colors.primary} /> : null} onClick={() => onPick(userAssignee(user))} />)}
				</SheetGroup>
			)}
			{teams.length === 0 && people.length === 0 && <p style={{ fontSize: 17, fontWeight: 600, color: colors.textSecondary, textAlign: 'center' }}>{'Aucune équipe ni personne pour « ' + query.trim() + ' »'}</p>}
		</BottomSheet>
	)
}

function InterventionForm({ kind, defaultUrgency, singleScroll, missing, inventoryPhotos, inventoryComments, pitch, stay, user, loadUsers, onCreate, onClose })
{
	const fixedType = kind === 'restock' ? 'Ménage' : (kind === 'repair' ? 'Technique' : null)
	// Un problème signalé en cours d'inventaire est le plus souvent technique : présélectionné, modifiable
	const initialType = fixedType || 'Technique'
	const [type, setType] = useState(initialType)
	const [checked, setChecked] = useState(() => missing.map(object => object.id))
	const [description, setDescription] = useState('')
	const [pickedPhotos, setPickedPhotos] = useState([])
	const [pickedComments, setPickedComments] = useState([])
	const [newPhotos, setNewPhotos] = useState([])
	const [assignee, setAssignee] = useState(defaultAssignee(initialType))
	// Un réassort ou une réparation est « Important » par défaut, comme toute intervention créée en tour
	// (defaultUrgency) ; un problème signalé pendant un inventaire demande de choisir
	const [urgency, setUrgency] = useState(defaultUrgency || (kind === 'restock' || kind === 'repair' ? 'IMPORTANT' : null))
	const [permission, setPermission] = useState(true)
	const [users, setUsers] = useState([])
	const [pickerOpen, setPickerOpen] = useState(false)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(false)

	useEffect(() => {
		let cancelled = false
		loadUsers().then(list => {
			if (!cancelled)
				setUsers(list)
		})
		return () => {
			cancelled = true
		}
	}, [loadUsers])

	// Nom de l'auteur d'un commentaire joint : celui qu'on connaît déjà, sinon celui de l'équipe (userId)
	const authorOf = (item) => {
		if (item.user)
			return item.user
		const person = users.find(candidate => String(candidate._id) === String(item.userId))
		return person ? userAssignee(person).name : ''
	}

	const checkedObjects = missing.filter(object => checked.includes(object.id))
	const finalDescription = kind === 'restock' ? restockDescription(checkedObjects) : description
	const blocker = formBlocker({ kind, type, description, checkedCount: checkedObjects.length, urgency })

	const chooseType = (value) => {
		setType(value)
		setAssignee(defaultAssignee(value))
	}

	const create = () => {
		if (blocker || saving)
			return ;
		setSaving(true)
		setError(false)
		const doc = buildIntervention({ kind, type, description: finalDescription, assignee, urgency, customerPermission: permission, pitch, stay, user, now: nowSeconds() })
		const photos = inventoryPhotos.filter(item => pickedPhotos.includes(item.photo.id)).map(item => item.photo).concat(newPhotos)
		const comments = inventoryComments.filter(item => pickedComments.includes(item.id)).map(item => ({ ...item, user: authorOf(item) }))
		doc.history = doc.history.concat(memoEvents(comments))
		Promise.resolve(onCreate({ kind, doc, photos, comments }))
			.catch(() => {
				setSaving(false)
				setError(true)
			})
	}

	const footer = (
		<React.Fragment>
			{error && <ErrorNote>L'intervention n'a pas pu être créée. Vérifiez la connexion, puis réessayez : votre saisie est gardée.</ErrorNote>}
			<PrimaryButton icon={saving || blocker ? null : <CheckIcon />} disabled={!!blocker || saving} onClick={create}>{saving ? 'Création…' : 'Créer l\'intervention'}</PrimaryButton>
			{blocker && !saving && <p style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary, textAlign: 'center' }}>{blocker}</p>}
		</React.Fragment>
	)

	return (
		<ActionSheet title={kindTitle(kind)} subtitle={pitch.location + (type ? ', intervention ' + type : '')} onClose={onClose} footer={footer} singleScroll={singleScroll}>
			{!fixedType && (
				<React.Fragment>
					<h3 style={sectionTitle}>Type</h3>
					<div style={{ display: 'flex', gap: 12 }}>
						<Pill selected={type === 'Ménage'} label="Ménage" onClick={() => chooseType('Ménage')} />
						<Pill selected={type === 'Technique'} label="Technique" onClick={() => chooseType('Technique')} />
					</div>
				</React.Fragment>
			)}

			{kind === 'restock' && (
				<React.Fragment>
					<h3 style={sectionTitle}>{'Objets à réapprovisionner (' + checkedObjects.length + ' sur ' + missing.length + ')'}</h3>
					<div style={{ borderBottom: '1px solid ' + colors.separator }}>
						{missing.map(object => (
							<CheckRow
								key={object.id}
								checked={checked.includes(object.id)}
								title={object.missing + ' ' + object.name}
								subtitle={object.theme}
								onClick={() => setChecked(current => (current.includes(object.id) ? current.filter(id => id !== object.id) : current.concat([object.id])))}
							/>
						))}
					</div>
				</React.Fragment>
			)}

			{kind !== 'restock' && (
				<React.Fragment>
					<label htmlFor="field-intervention-description" style={sectionTitle}>Description</label>
					<textarea
						id="field-intervention-description"
						rows={3}
						className="browser-default"
						value={description}
						placeholder={kind === 'repair' ? 'Ce qu\'il faut réparer' : 'Ce que vous avez constaté'}
						onChange={(e) => setDescription(e.target.value)}
						style={{ width: '100%', minHeight: 104, margin: 0, padding: '14px 16px', borderRadius: 18, border: '2px solid ' + colors.border, background: colors.surface, fontSize: 17, lineHeight: 1.45, fontFamily: 'inherit', color: colors.text, resize: 'vertical', boxSizing: 'border-box', flex: 'none' }}
					/>
					{inventoryComments.length > 0 && (
						<React.Fragment>
							<h3 style={sectionTitle}>Commentaires de l'emplacement</h3>
							<p style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary, marginTop: -8 }}>Touchez ceux à joindre.</p>
							<div style={{ borderBottom: '1px solid ' + colors.separator }}>
								{inventoryComments.map(item => (
									<CheckRow
										key={item.id}
										checked={pickedComments.includes(item.id)}
										title={item.text}
										subtitle={authorOf(item)}
										onClick={() => setPickedComments(current => (current.includes(item.id) ? current.filter(id => id !== item.id) : current.concat([item.id])))}
									/>
								))}
							</div>
						</React.Fragment>
					)}
					{inventoryPhotos.length > 0 && (
						<React.Fragment>
							<h3 style={sectionTitle}>Photos de l'inventaire</h3>
							<p style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary, marginTop: -8 }}>Touchez celles à joindre.</p>
							<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
								{inventoryPhotos.map((item, index) => {
									const selected = pickedPhotos.includes(item.photo.id)
									return (
										<button
											key={item.photo.id}
											type="button"
											aria-pressed={selected}
											aria-label={'Joindre la photo ' + (index + 1) + (item.photo.description ? ', ' + item.photo.description : '')}
											onClick={() => setPickedPhotos(current => (selected ? current.filter(id => id !== item.photo.id) : current.concat([item.photo.id])))}
											style={{ backgroundImage: 'none', backgroundColor: '#DDE1EA', position: 'relative', aspectRatio: '1 / 1', minHeight: 96, padding: 0, borderRadius: 12, border: '3px solid ' + (selected ? colors.primary : 'transparent'), overflow: 'hidden', cursor: 'pointer' }}
										>
											<img src={item.photo.thumb || item.photo.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
											{selected && (
												<span aria-hidden="true" style={{ position: 'absolute', top: 4, right: 4, width: 30, height: 30, borderRadius: 999, background: colors.primary, border: '2px solid #FFFFFF', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
													<CheckIcon size={18} />
												</span>
											)}
										</button>
									)
								})}
							</div>
						</React.Fragment>
					)}
					<h3 style={sectionTitle}>Nouvelles photos</h3>
					<FieldPhotoPicker photos={newPhotos} user={user} knownPhotos={inventoryPhotos.map(item => item.photo)} onChange={setNewPhotos} />
				</React.Fragment>
			)}

			{type && assignee && (
				<React.Fragment>
					<h3 style={sectionTitle}>Attribuer à</h3>
					<button
						type="button"
						className="field-row"
						onClick={() => setPickerOpen(true)}
						style={{ backgroundImage: 'none', backgroundColor: '#F2F3F7', minHeight: 64, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 16px 8px 20px', borderRadius: 18, border: 0, color: colors.text, fontSize: 18, fontWeight: 700, textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' }}
					>
						<span>{assignee.name}</span>
						<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 15, color: colors.primary }}>
							Changer
							<ChevronRight color={colors.primary} strokeWidth={2.8} />
						</span>
					</button>
				</React.Fragment>
			)}

			<h3 style={sectionTitle}>Urgence</h3>
			<div style={{ display: 'flex', gap: 8 }}>
				{URGENCIES.map(item => (
					<Pill
						key={item.value}
						selected={urgency === item.value}
						label={item.label}
						icon={item.value === 'NORMAL' ? null : <WarningIcon size={18} />}
						onClick={() => setUrgency(item.value)}
					/>
				))}
			</div>

			{type === 'Technique' && (
				<React.Fragment>
					<button
						type="button"
						role="switch"
						aria-checked={permission}
						onClick={() => setPermission(!permission)}
						style={{ backgroundImage: 'none', backgroundColor: 'transparent', minHeight: 64, width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '8px 0', border: 0, color: colors.text, textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer', marginTop: 8 }}
					>
						<span style={{ flex: 1, fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}>Le vacancier autorise l'équipe à entrer en son absence</span>
						<span aria-hidden="true" style={{ width: 64, height: 36, borderRadius: 999, background: permission ? colors.primary : '#B9C0D4', position: 'relative', flex: 'none', transition: 'background 150ms ease-out' }}>
							<span style={{ position: 'absolute', top: 3, left: permission ? 31 : 3, width: 30, height: 30, borderRadius: 999, background: '#FFFFFF', transition: 'left 150ms ease-out' }} />
						</span>
					</button>
					<p style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: colors.textSecondary, marginTop: -8 }}>
						<InfoIcon />
						Occupation de l'hébergement non confirmée
					</p>
				</React.Fragment>
			)}

			{pickerOpen && (
				<AssigneePicker
					value={assignee}
					users={users}
					onPick={(value) => {
						setAssignee(value)
						setPickerOpen(false)
					}}
					onClose={() => setPickerOpen(false)}
				/>
			)}
		</ActionSheet>
	)
}

// start : « choice » (tuile Créer une intervention) ou « problem » (Signaler un problème)
export default function FieldInterventionSheet({ start, defaultUrgency, singleScroll, missing, restockOffered, restockDone, inventoryPhotos, inventoryComments, pitch, stay, user, loadUsers, onCreate, onClose })
{
	const [kind, setKind] = useState(start === 'choice' ? null : start)

	if (kind)
		return <InterventionForm kind={kind} defaultUrgency={defaultUrgency} singleScroll={singleScroll} missing={missing} inventoryPhotos={inventoryPhotos || []} inventoryComments={inventoryComments || []} pitch={pitch} stay={stay} user={user} loadUsers={loadUsers} onCreate={onCreate} onClose={onClose} />

	return (
		<ActionSheet title="Créer une intervention" subtitle={pitch.location} onClose={onClose} singleScroll={singleScroll}>
			{(restockOffered || restockDone) && (
				<ChoiceRow
					title="Réassort"
					subtitle={restockDone ? 'Déjà créé : un seul réassort par inventaire' : plural(missing.length, 'objet manquant', 'objets manquants') + ', équipe ménage'}
					color="#0F766E"
					disabled={restockDone}
					onClick={() => setKind('restock')}
				/>
			)}
			<ChoiceRow title="Réparation" subtitle="Intervention Technique" color="#8124FF" onClick={() => setKind('repair')} />
			{!restockOffered && !restockDone && missing.length === 0 && <p style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>Aucun objet manquant : pas de réassort à prévoir.</p>}
		</ActionSheet>
	)
}
